import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Share2, Link as LinkIcon, X, Check } from 'lucide-react';

const buildShareUrl = (postId) => {
  const origin = (typeof window !== 'undefined' && window.location?.origin) || '';
  return `${origin}/post/${postId}`;
};

const ICON_SIZE = 'w-[18px] h-[18px]';

const platforms = (url, text) => [
  {
    key: 'whatsapp',
    label: 'WhatsApp',
    color: '#25D366',
    href: `https://api.whatsapp.com/send?text=${encodeURIComponent(`${text} ${url}`)}`,
    icon: (
      <svg viewBox="0 0 24 24" className={ICON_SIZE} fill="currentColor">
        <path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488 11.824 11.824 0 0 1 3.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 0 0 1.512 5.26l.16.255-1 3.648 3.741-.981.176.119zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z"/>
      </svg>
    ),
  },
  {
    key: 'facebook',
    label: 'Facebook',
    color: '#1877F2',
    href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    icon: (
      <svg viewBox="0 0 24 24" className={ICON_SIZE} fill="currentColor">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    ),
  },
  {
    key: 'twitter',
    label: 'X',
    color: '#0f1419',
    href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
    icon: (
      <svg viewBox="0 0 24 24" className={ICON_SIZE} fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
      </svg>
    ),
  },
  {
    key: 'instagram',
    label: 'Instagram',
    color: '#E1306C',
    href: null, // Instagram has no direct share URL — copy + open
    icon: (
      <svg viewBox="0 0 24 24" className={ICON_SIZE} fill="currentColor">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/>
      </svg>
    ),
  },
];

/**
 * SharePostMenu — popover with native share targets (WhatsApp, Facebook, X,
 * Instagram-copy) + Copy Link. Opens above its trigger button on mobile.
 */
export const SharePostMenu = ({ postId, postText = '', onClose, anchor = 'right' }) => {
  const [copied, setCopied] = useState(false);
  const url = buildShareUrl(postId);
  const text = (postText || '').slice(0, 140) || 'Check out this post on DCCF Network';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Could not copy link');
    }
  };

  const onPlatform = (p) => {
    if (p.key === 'instagram') {
      copy();
      window.open('https://instagram.com', '_blank', 'noopener');
      onClose?.();
      return;
    }
    if (p.href) {
      window.open(p.href, '_blank', 'noopener,noreferrer,width=600,height=540');
      onClose?.();
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 6 }}
        className={`absolute ${anchor === 'right' ? 'right-0' : 'left-0'} bottom-full mb-2 w-[260px] bg-white rounded-xl shadow-[0_10px_30px_-8px_rgba(15,23,42,0.20)] border border-slate-200 z-50 overflow-hidden`}
        data-testid="share-menu"
      >
        <div className="px-4 pt-3 pb-2 flex items-center justify-between border-b border-slate-100">
          <p className="text-[13px] font-semibold text-[#002249] flex items-center gap-1.5">
            <Share2 className="w-3.5 h-3.5" /> Share post
          </p>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-2 py-2">
          <button
            onClick={copy}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] text-[#050505] font-medium hover:bg-slate-50 transition-colors"
            data-testid="share-copy-link"
          >
            <span className="w-8 h-8 rounded-full bg-slate-100 text-[#002249] flex items-center justify-center">
              {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <LinkIcon className="w-4 h-4" />}
            </span>
            <span className="flex-1 text-left">{copied ? 'Copied!' : 'Copy link'}</span>
          </button>
          {platforms(url, text).map((p) => (
            <button
              key={p.key}
              onClick={() => onPlatform(p)}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] text-[#050505] font-medium hover:bg-slate-50 transition-colors"
              data-testid={`share-${p.key}`}
            >
              <span
                className="w-8 h-8 rounded-full flex items-center justify-center text-white"
                style={{ background: p.color }}
              >
                {p.icon}
              </span>
              <span className="flex-1 text-left">{p.label}</span>
            </button>
          ))}
        </div>
      </motion.div>
    </>
  );
};

export default SharePostMenu;
